import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlainMenuDto } from '@ordr-as/menu-builder-types/epos';
import { getGetMenuForLocationQueryKey } from '@ordr-as/menu-builder-types/epos';
import { useMenuSectionsActions } from '../context/MenuSectionsStore';
import { useOpeningHoursActions } from '../context/OpeningHoursStore';
import { mapPlainMenuToMenuData } from '../utils/menuMapper';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import { isValidNumericLocationId } from '../utils/locationId';

// Store ETags (menu version ID) by locationId
// If we specify an etag in the request to menu and it matches, the server can skip returning menu content
const etagCache = new Map<number, string>();
const timeOffsetCache = new Map<number, number>();

function updateTimeOffsetFromResponse(locationId: number, response: Response) {
  const dateHeader = response.headers.get('Date');
  if (!dateHeader) {
    return;
  }

  const serverTimeMs = Date.parse(dateHeader);
  if (Number.isNaN(serverTimeMs)) {
    return;
  }

  timeOffsetCache.set(locationId, serverTimeMs - Date.now());
}

export const useQueryMenuSections = (locationId?: number) => {
  const { setMenuData, setLoading } = useMenuSectionsActions();
  const { setAllOpeningHours, setServerTimeOffset } = useOpeningHoursActions();
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const locationIdRef = useRef(locationId);
  locationIdRef.current = locationId;

  const query = useQuery({
    enabled: isValidNumericLocationId(locationId),
    queryFn: async ({ signal }): Promise<PlainMenuDto> => {
      const id = locationIdRef.current;
      if (!id) throw new Error('locationId is required');

      const accessToken = await getAccessToken();
      const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
      };

      // Add If-None-Match header if we have a cached ETag
      const cachedEtag = etagCache.get(id);
      if (cachedEtag) {
        headers['If-None-Match'] = cachedEtag;
      }

      const response = await fetch(`${config.menuBuilderUrl}/public-api/v1/menus/${id}/plain?caller=epos`, {
        method: 'GET',
        headers,
        signal,
      });

      updateTimeOffsetFromResponse(id, response);

      // Handle 304 Not Modified - return React Query's cached data
      // This maintains referential equality, preventing re-renders and store updates
      if (response.status === 304) {
        const cachedData = queryClient.getQueryData<PlainMenuDto>(getGetMenuForLocationQueryKey(id));
        if (cachedData) return cachedData;
        throw new Error('304 but no cached data available');
      }

      if (!response.ok) {
        throw new Error(`Menu fetch failed: ${response.status} ${response.statusText}`);
      }

      const data: PlainMenuDto = await response.json();

      // Store ETag for future requests
      const etag = response.headers.get('ETag');
      if (etag) {
        etagCache.set(id, etag);
      }

      return data;
    },
    queryKey: getGetMenuForLocationQueryKey(locationId),
    refetchInterval: 5 * 60 * 1000,
    refetchOnMount: 'always',
    select: mapPlainMenuToMenuData,
  });

  useEffect(() => {
    if (locationId) {
      setLoading(true);
    }
  }, [locationId, setLoading]);

  useEffect(() => {
    const id = locationIdRef.current;
    if (!id) {
      return;
    }

    if (query.isError) {
      setLoading(false);
      return;
    }

    if (query.isSuccess && query.data) {
      setMenuData(
        query.data.activeSections,
        query.data.courses,
        query.data.searchableSections,
        query.data.componentProducts,
        id
      );
      setAllOpeningHours(query.data.openingHours);
      setServerTimeOffset(timeOffsetCache.get(id) ?? 0);
      setLoading(false);
    }
  }, [query.isError, query.isSuccess, query.data, setMenuData, setAllOpeningHours, setServerTimeOffset, setLoading]);

  return query;
};
